import { provinceAndStateCodes, type ProvinceOrStateCode } from 'utils/province_or_state';

/**
 * Reading an area slug, which is `${city}-${provinceCode}` with dashes for
 * spaces in the city — `ottawa-on`, `st-catharines-on`.
 */

/**
 * The province or state an area slug ends with: `ottawa-on` gives `on`, and
 * anything without a recognised trailing code gives `undefined`.
 *
 * Not `provinceOrStateCodeFromSlug`, which reads a province's own name slug
 * (`ontario`) and returns nothing for an area slug.
 */
export function provinceFromAreaSlug(slug: string): ProvinceOrStateCode | undefined {
  const trailing = slug.trim().toLowerCase().split('-').pop();
  return provinceAndStateCodes.find(code => code === trailing);
}

/**
 * The city name an area slug carries: `ottawa-on` gives `Ottawa`.
 *
 * Every word is capitalised, including short ones, so `niagara-on-the-lake-on`
 * gives `Niagara On The Lake`. That is not how the name is written, but it is
 * what the city-insights generation API has always produced, and the value is
 * matched against stored rows and passed to the stats service, so the spelling
 * is not ours to improve in passing.
 *
 * The result is a best effort either way. The real name comes from the address
 * service (an area page's own fetch uses its subdivision label), so callers
 * that need certainty should use that, and callers relying on a match should
 * cope with missing it.
 */
export function cityFromAreaSlug(slug: string): string {
  const parts = slug.trim().toLowerCase().split('-').filter(Boolean);
  const withoutProvince = provinceFromAreaSlug(slug) ? parts.slice(0, -1) : parts;
  return withoutProvince
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
